import React, { useState, useEffect, useRef, JSX } from 'react';
import { i18n } from '../../../i18n/translate';
import { WindowHelperWrapper } from '../../../utils';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { Icon, Layout, Typography } from '../../../core';
import { Box } from '@chakra-ui/react';
import { LanguageModalBody } from './LanguageModalBody';
import { LanguageModelProps } from './types';

export const LanguageModal: React.FC<LanguageModelProps> = ({
  appendLanguage,
  enforceSelection,
  languages,
  onConfirmSelection,
  selectedLanguage,
  showLanguageModal,
  toggleLanguageModal,
}) => {
  const containerRef = useRef(null);
  const [selected, setSelected] = useState<string>(selectedLanguage);
  const [isLoading, setLoading] = useState<boolean>(true);
  const windowHelper = WindowHelperWrapper();

  useEffect((): void => {
    isLoading && containerRef && containerRef.current && setLoading(false);
  }, [isLoading]);

  useEffect((): void => {
    i18n && i18n.loadTranslations();
  }, []);

  const confirmSelection = (): void => {
    if (typeof window === 'undefined') {
      return;
    }

    onConfirmSelection && onConfirmSelection(selected);
    toggleLanguageModal();
    localStorage.removeItem('languageChecked');
    if (!appendLanguage && languages[selected]?.translation_url) {
      windowHelper.redirect(sanitizeUrl(languages[selected]?.translation_url));
    } else {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('locale', selected.replace('_', '-'));

      windowHelper.redirect(encodeURI(sanitizeUrl(currentUrl.toString())));
    }
  };

  const onClose = (): void => {
    setSelected(selectedLanguage);
    toggleLanguageModal();
  };

  const renderedTitle: JSX.Element = (
    <Typography variant={'h2'}>
      <Box as={'span'} mr={2}>
        <Icon iconName="language" prefix="far" />
      </Box>
      {i18n.t('Choose Your Language')}
    </Typography>
  );

  if (!languages) return null;
  if (typeof languages !== 'object' || Object.keys(languages).length <= 0) return null;
  const confirmButtonDisabled = !enforceSelection && selected === selectedLanguage;

  return (
    <Layout ref={containerRef}>
      <LanguageModalBody
        buttonDisabled={confirmButtonDisabled}
        ref={!isLoading ? containerRef.current : null}
        renderedTitle={renderedTitle}
        onClose={onClose}
        showLanguageModal={showLanguageModal}
        enforceSelection={enforceSelection}
        languages={languages}
        confirmSelection={confirmSelection}
        selected={selected}
        setSelected={setSelected}
      />
    </Layout>
  );
};
